import { makeStyles } from '@material-ui/core/styles';

import { Flex, Tag, TagGroup, Text } from '@backstage/ui';

import {
  LabelUtils,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import { RuleFragment, getTypeOrNull } from '@spotify/confidence-graphql';
import {
  formatRuleId,
  getRuleTypeDisplayName,
} from '@spotify/plugin-confidence-flags-react';

const useStyles = makeStyles({
  ruleId: {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },
  ruleItem: {
    border: '1px solid var(--bui-border)',
    borderRadius: 'var(--bui-radius-2)',
    padding: 'var(--bui-space-2)',
  },
});

const getRuleVariantNames = (rule: RuleFragment) =>
  (rule.assignmentSpec?.assignments ?? [])
    .map(assignment => getTypeOrNull(assignment.variant?.variant)?.name)
    .filter((name): name is string => Boolean(name))
    .map(name => extractLastNameComponent(name) ?? name);

interface RuleItemProps {
  rule: RuleFragment;
}

export const RuleItem = ({ rule }: RuleItemProps) => {
  const classes = useStyles();
  const id = formatRuleId(rule.name);
  const ruleTypeRaw = LabelUtils.getLabelValue(rule.labels ?? [], 'ruleType');
  const ruleType =
    getRuleTypeDisplayName(ruleTypeRaw) || 'Conditional targeting';
  const variantNames = getRuleVariantNames(rule);
  const targetingKey = rule.targetingKeySelector;

  return (
    <Flex direction="column" gap="1" className={classes.ruleItem}>
      <Flex align="center" gap="2">
        <Text variant="body-small" weight="bold" className={classes.ruleId}>
          {id}
        </Text>
        <Text variant="body-small" color="secondary">
          {ruleType}
        </Text>
        {rule.enabled === false && (
          <Text variant="body-small" color="secondary">
            (disabled)
          </Text>
        )}
      </Flex>
      {rule.description && <Text variant="body-small">{rule.description}</Text>}
      {targetingKey && (
        <Flex align="center" gap="2">
          <Text variant="body-small" color="secondary">
            Randomized by
          </Text>
          <TagGroup aria-label="Randomization key">
            <Tag id={targetingKey} textValue={targetingKey} size="small">
              {targetingKey}
            </Tag>
          </TagGroup>
        </Flex>
      )}
      {variantNames.length > 0 && (
        <Text variant="body-small" color="secondary">
          {`Assigns: ${variantNames.join(', ')}`}
        </Text>
      )}
      {(() => {
        const environments = (rule.environments ?? [])
          .map(env => getTypeOrNull(env))
          .filter((env): env is NonNullable<typeof env> => Boolean(env));
        if (environments.length === 0) return null;
        return (
          <Flex align="center" gap="2">
            <Text variant="body-small" color="secondary">
              Environments:
            </Text>
            <TagGroup aria-label="Environments">
              {environments.map(env => {
                const envLabel = extractLastNameComponent(env.name) ?? env.name;
                return (
                  <Tag
                    key={env.name}
                    id={env.name}
                    textValue={envLabel}
                    size="small"
                  >
                    {envLabel}
                  </Tag>
                );
              })}
            </TagGroup>
          </Flex>
        );
      })()}
    </Flex>
  );
};
